import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Pencil, Copy, Trash2, ArrowUpRight } from "lucide-react";

const GAP = 250, DEPTH = 190, ROT = 38;

export default function CoverFlow({ items, index, setIndex, renderCard, onOpen, onNew, onRename, onDuplicate, onDelete, accent = "#fbbf24", kindLabel = "project", newLabel = "New" }) {
  const [opening, setOpening] = useState(null);
  const all = [...items, { id: "__new", isNew: true }];
  const drag = useRef(null);
  const clamp = (i) => Math.max(0, Math.min(all.length - 1, i));

  useEffect(() => {
    const k = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight") setIndex((i) => clamp(i + 1));
      if (e.key === "ArrowLeft") setIndex((i) => clamp(i - 1));
      if (e.key === "Enter") activate(all[index]);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }); // eslint-disable-line

  const activate = (it) => {
    if (!it) return;
    if (it.isNew) return onNew();
    setOpening(it.id);
    setTimeout(() => onOpen(it), 480);
  };
  const cur = all[index];

  return (
    <div className="flex flex-col h-full select-none" data-testid="coverflow">
      <div className="coverflow flex-1 relative min-h-0 cf-in" data-testid="coverflow-stage"
        onWheel={(e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) { e.preventDefault(); setIndex((i) => clamp(i + Math.sign(e.deltaX || e.deltaY))); } }}
        onPointerDown={(e) => { drag.current = { x: e.clientX, i: index }; }}
        onPointerMove={(e) => { const d = drag.current; if (!d) return; const di = Math.round((d.x - e.clientX) / 140); if (di) { setIndex(clamp(d.i + di)); } }}
        onPointerUp={() => { drag.current = null; }} onPointerLeave={() => { drag.current = null; }}>
        <div className="absolute inset-x-0 bottom-[18%] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        {all.map((it, i) => {
          const off = i - index, a = Math.abs(off), side = Math.sign(off);
          const t = `translate3d(${off * GAP + side * 60}px, 0, ${-a * DEPTH}px) rotateY(${-side * Math.min(a, 1) * ROT}deg) scale(${a ? 0.88 : 1})`;
          return (
            <div key={it.id} data-testid={it.isNew ? "cf-new-card" : `cf-card-${it.id}`}
              className={`cf-card absolute left-1/2 top-1/2 -ml-[130px] -mt-[190px] w-[260px] cursor-pointer ${opening === it.id ? "opening" : ""}`}
              style={{ transform: t, zIndex: 100 - a, opacity: a > 4 ? 0 : 1, filter: a ? `brightness(${Math.max(0.35, 1 - a * 0.22)})` : "none", pointerEvents: a > 4 ? "none" : "auto" }}
              onClick={() => (off === 0 ? activate(it) : setIndex(i))}>
              <div className="rounded-2xl overflow-hidden border shadow-[0_40px_90px_-20px_rgba(0,0,0,.9)] bg-[#141518]" style={{ borderColor: off === 0 ? accent + "88" : "rgba(255,255,255,.08)" }}>
                {it.isNew ? (
                  <div className="h-[220px] flex flex-col items-center justify-center gap-3 text-zinc-400 border-2 border-dashed border-white/10 m-3 rounded-xl">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: accent + "22", color: accent }}><Plus className="w-7 h-7" /></div>
                    <div className="text-sm">{newLabel}</div>
                  </div>
                ) : <div className="h-[220px]">{renderCard(it)}</div>}
                <div className="px-4 py-3 border-t border-white/5">
                  <div className="text-sm text-white truncate">{it.isNew ? "Start fresh" : it.name}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{it.isNew ? `Create a new ${kindLabel}` : `Edited ${timeAgo(it.updated_at)}`}</div>
                </div>
              </div>
              <div className="cf-reflect mt-1 h-[80px] overflow-hidden rounded-2xl opacity-60 pointer-events-none"><div className="h-[220px] bg-[#141518]">{!it.isNew && renderCard(it)}</div></div>
            </div>
          );
        })}
        <button onClick={() => setIndex((i) => clamp(i - 1))} disabled={index === 0} data-testid="cf-prev" className="absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center disabled:opacity-20 z-[200]"><ChevronLeft className="w-5 h-5" /></button>
        <button onClick={() => setIndex((i) => clamp(i + 1))} disabled={index === all.length - 1} data-testid="cf-next" className="absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center disabled:opacity-20 z-[200]"><ChevronRight className="w-5 h-5" /></button>
      </div>

      <div className="h-24 shrink-0 flex flex-col items-center justify-center gap-3">
        <div className="flex items-center gap-2">
          {cur && !cur.isNew ? (
            <>
              <button onClick={() => activate(cur)} data-testid="cf-open" className="h-10 px-5 rounded-lg text-sm font-medium text-zinc-900 flex items-center gap-2 hover:brightness-110" style={{ background: accent }}><ArrowUpRight className="w-4 h-4" />Open</button>
              <button onClick={() => onRename(cur)} data-testid="cf-rename" className="h-10 px-4 rounded-lg text-sm border border-white/10 hover:bg-white/5 flex items-center gap-2"><Pencil className="w-4 h-4" />Rename</button>
              <button onClick={() => onDuplicate(cur)} data-testid="cf-duplicate" className="h-10 px-4 rounded-lg text-sm border border-white/10 hover:bg-white/5 flex items-center gap-2"><Copy className="w-4 h-4" />Duplicate</button>
              <button onClick={() => onDelete(cur)} data-testid="cf-delete" className="h-10 px-4 rounded-lg text-sm border border-white/10 hover:bg-red-500/10 text-red-400 flex items-center gap-2"><Trash2 className="w-4 h-4" />Delete</button>
            </>
          ) : (
            <button onClick={onNew} data-testid="cf-create" className="h-10 px-5 rounded-lg text-sm font-medium text-zinc-900 flex items-center gap-2 hover:brightness-110" style={{ background: accent }}><Plus className="w-4 h-4" />{newLabel}</button>
          )}
        </div>
        <div className="flex gap-1.5">{all.map((it, i) => <button key={it.id} onClick={() => setIndex(i)} className={`h-1.5 rounded-full transition-all ${i === index ? "w-6" : "w-1.5 bg-white/20 hover:bg-white/40"}`} style={i === index ? { background: accent } : {}} />)}</div>
      </div>
    </div>
  );
}

export function timeAgo(iso) {
  if (!iso) return "just now";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}
